import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PeriodTabs, NotConnected, Freshness, fmtN, pct, evoCls } from "./common";

function KpiCard({ label, value, evo, invert, testid }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={testid}>
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <p className="font-heading text-2xl font-extrabold font-mono tabular-nums mt-1" data-testid={`${testid}-value`}>{value}</p>
      <p className={`text-xs font-semibold mt-0.5 ${evoCls(evo, invert)}`} data-testid={`${testid}-evo`}>
        {evo == null ? "pas de comparaison" : `Évolution : ${pct(evo)}`}
      </p>
    </div>
  );
}

export default function SeoOverview({ goParams }) {
  const [period, setPeriod] = useState("28");
  const [status, setStatus] = useState(null);
  const [dash, setDash] = useState(null);
  useEffect(() => { api.get("/seo/gsc/status").then((r) => setStatus(r.data)).catch(() => setStatus({ error: true })); }, []);
  useEffect(() => {
    if (!status?.connected) return;
    setDash(null);
    api.get(`/seo/gsc/dashboard?period=${period}`).then((r) => setDash(r.data)).catch(() => setDash({ error: true }));
  }, [period, status]);
  if (!status) return <p className="text-sm text-slate-400">Chargement…</p>;
  if (status.error) return <p className="text-sm text-rose-600 rounded-xl border border-rose-200 bg-rose-50 p-4" data-testid="seo-status-error">Statut Search Console indisponible pour le moment — le reste de l'application fonctionne normalement.</p>;
  if (!status.connected) return <NotConnected goParams={goParams} />;
  const t = dash?.totals;
  const maxClicks = Math.max(1, ...(dash?.series || []).map((s) => s.clicks));
  return (
    <div className="space-y-4" data-testid="seo-overview">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <PeriodTabs value={period} onChange={setPeriod} />
        <Freshness lastSync={dash?.last_sync || status.last_sync} />
      </div>
      {!dash && <p className="text-sm text-slate-400">Chargement des statistiques…</p>}
      {dash?.error && <p className="text-sm text-rose-600" data-testid="seo-dash-error">Statistiques indisponibles pour le moment — la synchronisation sera retentée automatiquement.</p>}
      {dash && !dash.error && !dash.has_data && (
        <p className="text-sm text-slate-500 rounded-xl border border-slate-200 bg-white p-4" data-testid="seo-dash-empty">
          Aucune donnée sur cette période pour l'instant. Lancez une synchronisation depuis l'onglet Paramètres.
        </p>
      )}
      {dash && !dash.error && dash.has_data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="seo-kpis">
            <KpiCard label="Clics Google" value={fmtN(t.clicks)} evo={dash.evolution.clicks} testid="seo-kpi-clicks" />
            <KpiCard label="Impressions" value={fmtN(t.impressions)} evo={dash.evolution.impressions} testid="seo-kpi-impressions" />
            <KpiCard label="CTR moyen" value={`${(t.ctr * 100).toFixed(1)} %`} evo={dash.evolution.ctr} testid="seo-kpi-ctr" />
            <KpiCard label="Position moyenne" value={t.position} evo={dash.evolution.position} invert testid="seo-kpi-position" />
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="seo-chart">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Clics par jour</p>
            <div className="flex items-end gap-[2px] h-32">
              {dash.series.map((s) => (
                <div key={s.date} title={`${s.date} : ${s.clicks} clic(s), ${fmtN(s.impressions)} impression(s)`}
                  className="flex-1 bg-sky-500/80 rounded-t-sm min-w-[3px] hover:bg-sky-600 transition-colors"
                  style={{ height: `${Math.max(2, (s.clicks / maxClicks) * 100)}%` }} />
              ))}
            </div>
            <div className="flex justify-between text-[10px] text-slate-400 mt-1">
              <span>{dash.start}</span><span>{dash.end}</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
